// React and routing
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

// Styles
import "react-datepicker/dist/react-datepicker.css";
import "./list.css";
import "@sendbird/uikit-react/dist/index.css";

// Utilities and configurations
import FastAPIClient from "../../client";
import config from "../../config";

// Internationalization
import { FormattedMessage, IntlProvider } from "react-intl";
import messages_en from "./translations/en.json";
import messages_fr from "./translations/fr.json";

import { HiOutlineDocumentCurrencyEuro } from "react-icons/hi2";
import { PiCarProfileLight } from "react-icons/pi";

// Components
import Unauthorized from "../unauthorized/index/index";
import Product from "../../components/LayoutComponents/Product";
import NBAlist from "../../components/ListComponents/NBAlist/NBAlist";
import CustomerDetails from "../../components/LayoutComponents/CustomerDetails/CustomerDetails";
import Loader from "../../components/Loader";
import CollapseComponent from "../../components/CollapseComponents/CollapseComponent";
import BackButtonNew from "../../components/Buttons/BackButtonNew";
import BackButton from "../../components/Buttons/BackButton";
import OrderHistory from "../../components/LayoutComponents/OrderHistory";
import VisitHistory from "../../components/LayoutComponents/VisitHistory";
import ClientOrders from "../../components/LayoutComponents/ClientOrders";
import ReportForm from "../../components/LayoutComponents/ReportForm";
import ReminderModal from "../../components/ModalComponents/ReminderModal";
import ClientStatistics from "../../components/LayoutComponents/ClientStatistics";
import MeetingModal from "../../components/ModalComponents/MeetingModal";
import Channel from "../../components/Chat/Channel";
import { useOfflineMeetings } from "../../components/OfflineContext";
import CadencierPopUp from "../../components/ModalComponents/CadencierPopUp/index";
import ClientStatisticsPopUp from "../../components/ModalComponents/ClientStatisticsPopUp";
import FacturePopUp from "../../components/ModalComponents/FacturePopUp/index";
import CustomerContracts from "../../components/LayoutComponents/CustomerContracts/CustomerContracts";
import CustomerVisits from "../../components/LayoutComponents/CustomerVisits/CustomerVisits";

import { formatReports } from "../../utils/Reports/functions";
import {
    blankMeetingData,
    formatClientMeeting,
} from "../../utils/Meetings/functions";

// Icons
import {
    FiEdit,
    FiShoppingBag,
    FiShoppingCart,
    FiShuffle,

    // FiFileText,
    FiBell,
    // FiAlertTriangle,
    FiCalendar,
    FiMessageSquare,
} from "react-icons/fi";

import { HiOutlineSparkles } from "react-icons/hi2";

import {
    CACHE_RESPONSE_API,
    CACHE_RESPONSE_DATA,
} from "../../sw/utils/constants";
import { getDataFromStore } from "../../sw/utils/db";
import { useNetwork } from "../../components/NetworkProvider";
import { Button } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Initialize client and messages
const client = new FastAPIClient(config);

const messages = {
    en: messages_en,
    fr: messages_fr,
};

const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(false);

    useEffect(() => {
        const media = window.matchMedia(query);
        setMatches(media.matches);

        const handler = (event) => setMatches(event.matches);
        media.addListener(handler);

        return () => media.removeListener(handler);
    }, [query]);

    return matches;
};

const ClientPage = () => {
    const { clientId } = useParams();
    const [authorized, setAuthorized] = useState(false);
    const [clientInfo, setClientInfo] = useState([]);
    const [product, setProductRecos] = useState([]);
    const [list, setList] = useState([]);
    const [meetinghistory, setClientMeetingHistory] = useState([]);
    const [clientorders, setClientOrders] = useState([]);
    const [clientreceipts, setClientReceipts] = useState([]);
    const [reportlayout, setReportLayout] = useState([]);
    const [clientstats, setClientStats] = useState([]);
    const targetDivRef = useRef(null);
    const [refreshing, setRefreshing] = useState(true);
    const [openmodal, setOpenModal] = useState(false);
    const [locale, setLocale] = useState();
    const [user, setUser] = useState(false);
    const [shopURL, setUrl] = useState([]);
    const [reportToggle, setReportToggle] = useState(false);
    const [reminderToggle, setReminderToggle] = useState(false);
    const [message, setMessage] = useState(null); // State to store message
    const [chatMessages, setChatMessage] = useState([]);
    const [messageType, setMessageType] = useState(null); // Type of message (success or error)
    const { isOnline } = useNetwork();
    const [usersData, setUsersData] = useState([]);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const meetingId = queryParams.get("meeting_id"); // Extract meeting_id
    const meeting_type_id = queryParams.get("meeting_type_id"); // Extract meeting_id
    const { offlineMeetings, offlineRecommendations, offlineReports } =
        useOfflineMeetings();

    const isMdOrLarger = useMediaQuery("(min-width: 768px)");

    const [newEventData, setNewEventData] = useState(blankMeetingData);
    const [selectedTab, setSelectedTab] = useState(0);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    // fetch user data (for meeting)
    const fetchUsersData = async () => {
        const data = await client.getUsers();
        setUsersData(data.results);
    };

    // concat offlineMeetings with meetinghistory
    const totalMeetings = useMemo(() => {
        if (
            (offlineMeetings.length || offlineReports.length) &&
            clientId &&
            user?.user_id
        ) {
            // Filter offlineMeetings by clientId
            const currentClientsMeetings = offlineMeetings.filter(
                (item) => item?.client_id === clientId
            );
            let recurrentClientsReports = offlineReports.filter(
                (item) => item?.client_id === clientId
            );
            const formatList = formatReports(recurrentClientsReports, user);

            if (meetinghistory.length) {
                let mergeMeetings = meetinghistory.concat(
                    currentClientsMeetings,
                    formatList
                );
                return mergeMeetings.sort(
                    (a, b) => new Date(a.start_date) - new Date(b.start_date)
                );
            }

            return currentClientsMeetings
                .concat(formatList)
                .sort(
                    (a, b) => new Date(a.start_date) - new Date(b.start_date)
                );
        }
        return meetinghistory;
    }, [offlineMeetings, meetinghistory, offlineReports, clientId, user]);
    const openModal = () => {
        setNewEventData(formatClientMeeting(clientInfo, user));
        setOpenModal(true); // This navigates back one step in the history
    };
    const closeModal = () => {
        setOpenModal(false); // This navigates back one step in the history
    };

    useEffect(() => {
        setLocale(localStorage.getItem("language"));
        const storedLayoutData = localStorage.getItem("layoutData");
        if (storedLayoutData) {
            try {
                const parsedData = JSON.parse(storedLayoutData);
                setUrl(parsedData); // Store the parsed object in state
            } catch (error) {
                console.error(
                    "Error parsing layoutData from localStorage:",
                    error
                );
            }
        }
    }, []);

    const navigate = useNavigate();

    const handleGoBack = () => {
        navigate(-1); // This navigates back one step in the history
    };

    const handleStartVisit = () => {
        setReportToggle(true);
    };

    const handleSaveVisit = () => {
        setReportToggle(false);

        getClientInfo(user.user_id, clientId);
    };

    const handleSetReminder = () => {
        setReminderToggle(true);
    };

    useEffect(() => {
        getUserName();
        fetchUsersData();
    }, []);

    useEffect(() => {
        // Scroll to the start of the target div when 'toggle' changes
        if (reportToggle && targetDivRef.current) {
            setTimeout(() => {
                targetDivRef.current.scrollIntoView({ behavior: "smooth" });
            }, 100); // Adjust the delay as needed a
        }
    }, [reportToggle, targetDivRef]);

    // Monitor whether the current user's recommendation information is updated offline
    const totalList = useMemo(() => {
        if (list.length) {
            if (offlineRecommendations.length) {
                return list.filter(
                    (item) =>
                        !offlineRecommendations.some(
                            (recommendation) =>
                                recommendation.client_id === item.client_id &&
                                recommendation.task_id == item.task_id
                        )
                );
            }
            return list;
        }
        return [];
    }, [list, offlineRecommendations]);

    useEffect(() => {
        if (user.user_id && clientId) {
            getClientInfo(user.user_id, clientId);

            getReportLayout();

            targetDivRef.current = document.getElementById("yourTargetDivId");
        }
    }, [user, clientId, isOnline]);

    const getUserName = () => {
        client.fetchUser().then((data) => {
            setUser(data);
        });
    };

    const getAllOfflineClients = () => {
        const storeName =
            CACHE_RESPONSE_DATA[CACHE_RESPONSE_API.CLIENTS].storeName;
        const url = `${config.apiBasePath}${CACHE_RESPONSE_API.CLIENTS}?user_id=${user.user_id}`;
        getDataFromStore(storeName, url)
            .then((result) => {
                if (result?.data?.length) {
                    const currentClients = result?.data?.filter(
                        (item) => item.client_id === clientId
                    );
                    handleClientDetailInfo(currentClients);
                }
                setAuthorized(true);
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });
    };

    const getClientInfo = (userId, clientId) => {
        if (isOnline) {
            client
                .getUserClients(userId, clientId)
                .then((data) => {
                    handleClientDetailInfo(data);
                    setAuthorized(true);
                })
                .catch((error) => {
                    console.error("Error fetching client data:", error);
                    // Check if it's an authorization error (403)
                    if (error.response && error.response.status === 403) {
                        setAuthorized(false);
                        setRefreshing(false);
                        return;
                    }
                    // Handle other errors as before
                    setRefreshing(false);
                });
        } else {
            getAllOfflineClients();
        }
    };

    const handleClientDetailInfo = (data) => {
        setClientInfo(data[0]);
        setClientStats(data[0].statistics);
        setClientReceipts(data[0].receipts);
        setProductRecos(data[0].product_recommendations);
        setList(data[0].next_best_actions);
        setClientOrders(data[0].orders);
        setClientMeetingHistory(data[0].meetings);
        setRefreshing(false);
    };

    const getReportLayout = () => {
        client.getReportLayout().then((data) => {
            setReportLayout(data);
        });
    };

    // const handleSendMail = async () => {
    //     try {
    //         // Attempt to send email
    //         await client.sendMail({
    //             to: "tobi@myway.technology",
    //             subject: `Demande de dépannage pour ${clientInfo.client_name}`,
    //             content: `Hello ${clientInfo.client_name},\n\nThis is a message sent from your application.`,
    //             client_id: clientId,
    //             user_id: user.user_id,
    //         });

    //         // If email is sent successfully
    //         setMessage("Email envoyé avec succès!"); // Success message in French
    //         setMessageType("success");

    //         // Automatically hide the message after 2 seconds
    //         setTimeout(() => {
    //             setMessage(null);
    //         }, 2000);
    //     } catch (error) {
    //         // If there's an error sending the email
    //         setMessage("Une erreur s'est produite. Veuillez réessayer."); // Error message in French
    //         setMessageType("error");

    //         // Automatically hide the message after 2 seconds
    //         setTimeout(() => {
    //             setMessage(null);
    //         }, 2000);
    //     }
    // };

    const [successMessage, setSuccessMessage] = useState(""); // Track success message
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(""), 3000); // Hide message after 3 seconds
            return () => clearTimeout(timer); // Cleanup on unmount
        }
    }, [successMessage]);

    const [errorMessage, setErrorMessage] = useState(""); // Track success message
    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => setErrorMessage(""), 3000); // Hide message after 3 seconds
            return () => clearTimeout(timer); // Cleanup on unmount
        }
    }, [errorMessage]);

    // CADENCIER & STATS POP UP SHOW
    const [showStatsPopUp, setShowStatsPopUp] = useState(false);
    const [showCadencierPopUp, setShowCadencierPopUp] = useState(false);
    const [showFacturePopUp, setShowFacturePopUp] = useState(false);

    if (refreshing) {
        return <Loader />; // Display Loader while data is being fetched
    }

    if (!authorized) {
        return <Unauthorized />;
    }

    return (
        <section className="flex flex-row md:h-full h-[88%] mt-4">
            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                }}
            >
                <IntlProvider locale={locale} messages={messages[locale]}>
                    <div className="block md:hidden">
                        <BackButton handleGoBack={handleGoBack} />
                    </div>
                    <div className="flex flex-row items-center mt-5 xl:ml-2">
                        <BackButtonNew handleGoBack={handleGoBack} />
                        <div className="ml-2 text-left text-black bg-white w-full md:w-2/3">
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                component="span"
                            >
                                {clientInfo?.client_name
                                    ?.replace(/"/g, "")
                                    .replace(
                                        /\w\S*/g,
                                        (txt) =>
                                            txt.charAt(0).toUpperCase() +
                                            txt.substr(1).toLowerCase()
                                    )}
                                ,{" "}
                            </Typography>
                            <div className="block md:inline-block">
                                <span className="font-normal text-md">
                                    {clientInfo?.city
                                        ?.replace(/"/g, "")
                                        .replace(
                                            /\w\S*/g,
                                            (txt) =>
                                                txt.charAt(0).toUpperCase() +
                                                txt.substr(1).toLowerCase()
                                        )}
                                    ,
                                </span>
                                <span className="font-normal text-md ml-2">
                                    {clientInfo?.client_id}
                                </span>
                                {clientInfo?.is_seasonal && (
                                    <span className="ml-4 px-2 py-1 text-xs text-white bg-amber-400 rounded-full">
                                        Saisonnier
                                    </span>
                                )}
                                {!clientInfo?.is_active && (
                                    <span className="ml-2 px-2 py-1 text-xs text-white bg-red-500 rounded-full">
                                        Inactif
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-row w-full sm:mt-5 sm:mb-5 md:mb-2 justify-end gap-x-4 pr-4 hidden md:flex">
                            <Button
                                onClick={() => handleSetReminder()}
                                variant="contained"
                                startIcon={<FiBell size={16} />}
                                sx={{
                                    backgroundColor: "white",
                                    fontWeight: "bold",
                                    color: "#black",
                                    border: "1px solid #EBEBEB",
                                    "&:hover": { backgroundColor: "#333333" },
                                }}
                            >
                                Rappel
                            </Button>

                            <Button
                                onClick={openModal}
                                variant="contained"
                                startIcon={<FiCalendar size={16} />}
                                sx={{
                                    backgroundColor: "white",
                                    fontWeight: "bold",
                                    color: "black",
                                    border: "1px solid #EBEBEB",
                                    "&:hover": { backgroundColor: "#333333" },
                                }}
                            >
                                Planifier une visite
                            </Button>

                            <Button
                                onClick={() => {
                                    if (shopURL?.shop_url) {
                                        window.open(shopURL.shop_url, "_blank");
                                    } else {
                                        console.error(
                                            "shopURL.shop_url is missing or undefined!"
                                        );
                                    }
                                }}
                                variant="contained"
                                startIcon={<FiShoppingBag size={16} />}
                                sx={{
                                    backgroundColor: "white",
                                    fontWeight: "bold",
                                    color: "black",
                                    border: "1px solid #EBEBEB",
                                    "&:hover": { backgroundColor: "#333333" },
                                }}
                            >
                                Portail
                            </Button>
                            <Button
                                onClick={() => handleStartVisit()}
                                variant="contained"
                                startIcon={<FiEdit size={16} />}
                                sx={{
                                    backgroundColor: "#C3FF36",
                                    fontWeight: "bold",
                                    color: "black",
                                    "&:hover": { backgroundColor: "#DAA520" },
                                }}
                            >
                                Compte rendu
                            </Button>
                        </div>
                    </div>

                    <Box
                        sx={{
                            width: "100%",
                            typography: "body1",
                            overflowX: "auto",
                        }}
                    >
                        <Tabs
                            value={selectedTab}
                            onChange={handleTabChange}
                            aria-label="client details tabs"
                            sx={{ paddingLeft: 2 }}
                            variant="scrollable"
                        >
                            <Tab
                                label={
                                    <FormattedMessage id="client.overview" />
                                }
                            />

                            <Tab
                                label={
                                    <FormattedMessage id="client.interactions_tab" />
                                }
                            />
                            <Tab
                                label={
                                    <FormattedMessage id="client.statistics" />
                                }
                            />
                            <Tab
                                label={
                                    <FormattedMessage id="client.invoices" />
                                }
                            />

                            <Tab
                                label={
                                    <FormattedMessage id="client.informations" />
                                }
                            />
                            <Tab
                                label={
                                    <FormattedMessage id="client.documents" />
                                }
                            />
                        </Tabs>
                        <Box>
                            {selectedTab === 0 && (
                                <div className="flex flex-col w-full">
                                    <div className="hidden md:block">
                                        <ClientStatistics
                                            statistics={clientstats}
                                            onNavigateToTab={() =>
                                                handleTabChange(null, 2)
                                            }
                                        />
                                    </div>

                                    <div className="flex flex-col md:flex-row w-full xl:pr-2 xl:pl-2 mt-4">
                                        <div className="float-left md:w-1/4 flex-col hidden md:block">
                                            <CollapseComponent
                                                text={
                                                    <FormattedMessage
                                                        id="client.contracts"
                                                        values={{ number: 1 }}
                                                    />
                                                }
                                                icon={
                                                    <HiOutlineDocumentCurrencyEuro />
                                                }
                                                maximize={false}
                                                open={true}
                                                toggle={false}
                                            >
                                                {clientInfo && (
                                                    <CustomerContracts
                                                        customer={clientInfo}
                                                    />
                                                )}
                                            </CollapseComponent>
                                            <CollapseComponent
                                                text={
                                                    <FormattedMessage
                                                        id="client.coverage"
                                                        values={{ number: 1 }}
                                                    />
                                                }
                                                icon={<PiCarProfileLight />}
                                                maximize={false}
                                                open={true}
                                                toggle={false}
                                            >
                                                {clientInfo && (
                                                    <CustomerVisits
                                                        customer={clientInfo}
                                                    />
                                                )}
                                            </CollapseComponent>
                                        </div>

                                        <div className="flex flex-col float-left md:w-1/2">
                                            <CollapseComponent
                                                text={
                                                    <FormattedMessage
                                                        id="client.nba"
                                                        values={{ number: 1 }}
                                                    />
                                                }
                                                icon={<HiOutlineSparkles />}
                                                maximize={false}
                                                open={true}
                                            >
                                                <ul className="display-inline text-gray-700 mb-5 text-sm pl-4 pr-4 w-full">
                                                    {totalList.filter(
                                                        (item) => !item.read
                                                    ).length === 0 ? (
                                                        <h1>
                                                            <FormattedMessage id="client.notasks" />
                                                        </h1>
                                                    ) : (
                                                        totalList
                                                            .filter(
                                                                (item) =>
                                                                    !item.read
                                                            ) // Only include items where read is False
                                                            .map((item) => (
                                                                <NBAlist
                                                                    recommendation={
                                                                        item
                                                                    }
                                                                    key={
                                                                        item.task_id
                                                                    }
                                                                    getRecommendations={
                                                                        getClientInfo
                                                                    }
                                                                    clientInfo={
                                                                        clientInfo
                                                                    }
                                                                    client_id={
                                                                        clientId
                                                                    }
                                                                    userId={
                                                                        user.user_id
                                                                    }
                                                                />
                                                            ))
                                                    )}
                                                </ul>
                                            </CollapseComponent>
                                            <div className="block md:hidden">
                                                <CollapseComponent
                                                    text={
                                                        <FormattedMessage
                                                            id="client.product_reco"
                                                            values={{
                                                                number: 1,
                                                            }}
                                                        />
                                                    }
                                                    icon={<FiShuffle />}
                                                    maximize={false}
                                                    open={true}
                                                >
                                                    {product.length > 0 ? (
                                                        product.map((prod) => (
                                                            <Product
                                                                product={prod}
                                                                key={
                                                                    prod.product_id
                                                                }
                                                            />
                                                        ))
                                                    ) : (
                                                        <div className="pr-3 pl-4 text-md text-sm">
                                                            <FormattedMessage id="client.no_product_reco" />
                                                        </div>
                                                    )}
                                                </CollapseComponent>
                                            </div>
                                            <CollapseComponent
                                                text={
                                                    <FormattedMessage
                                                        id="client.products"
                                                        values={{ number: 1 }}
                                                    />
                                                }
                                                icon={<FiShoppingCart />}
                                                maximize={isMdOrLarger}
                                                toggle={true}
                                                open={true}
                                                tooltipText={
                                                    "Produits commandés plus de 4 fois"
                                                }
                                                showPopUp={showCadencierPopUp}
                                                setShowPopUp={
                                                    setShowCadencierPopUp
                                                }
                                            >
                                                <ClientOrders
                                                    clientorders={clientorders}
                                                />
                                            </CollapseComponent>
                                        </div>

                                        <div className="flex flex-col overflow-hidden touch-none float-left md:w-1/4">
                                            <div className="hidden md:block">
                                                <CollapseComponent
                                                    text={
                                                        <FormattedMessage
                                                            id="client.product_reco"
                                                            values={{
                                                                number: 1,
                                                            }}
                                                        />
                                                    }
                                                    icon={<FiShuffle />}
                                                    maximize={false}
                                                    open={true}
                                                >
                                                    {product.length > 0 ? (
                                                        product.map((prod) => (
                                                            <Product
                                                                product={prod}
                                                                key={
                                                                    prod.product_id
                                                                }
                                                            />
                                                        ))
                                                    ) : (
                                                        <div className="pr-3 pl-4 text-md text-sm">
                                                            <FormattedMessage id="client.no_product_reco" />
                                                        </div>
                                                    )}
                                                </CollapseComponent>
                                            </div>

                                            <div className="hidden md:block ">
                                                <div className="overflow-hidden w-full mb-3">
                                                    <div className="ml-1 mr-3 md:mr-0 border border-gray-200 bg-white rounded-md">
                                                        <div className="flex flex-row w-full pl-2 items-center mb-1 mt-2">
                                                            <div className="flex w-8 h-8 ml-1 rounded-full items-center justify-center">
                                                                <FiMessageSquare />
                                                            </div>
                                                            <div className="flex text-lg md:text-base font-semibold text-black text-left ml-2 md:w-4/6 w-full bg-white">
                                                                Chat
                                                            </div>
                                                        </div>
                                                        <div className="h-60">
                                                            <Channel
                                                                client_id={
                                                                    clientInfo.client_id
                                                                }
                                                                currentUser={
                                                                    user.user_id
                                                                }
                                                                clientName={
                                                                    clientInfo.client_name
                                                                }
                                                                header={false}
                                                                isOnline={
                                                                    isOnline
                                                                }
                                                                messages={
                                                                    chatMessages
                                                                }
                                                                setMessages={
                                                                    setChatMessage
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {selectedTab === 3 && (
                                <div className="flex flex-col md:flex-row w-full xl:pr-2 xl:pl-2 mt-4">
                                    <div className="float-left w-full flex-col">
                                        <OrderHistory
                                            orderhistory={clientreceipts}
                                        />
                                    </div>
                                </div>
                            )}
                            {selectedTab === 1 && (
                                <div className="flex flex-col md:flex-row w-full xl:pr-2 xl:pl-2 mt-4">
                                    <div className="float-left w-full flex-col">
                                        <VisitHistory
                                            visithistory={totalMeetings}
                                            reportLayout={reportlayout}
                                        />
                                    </div>
                                </div>
                            )}
                            {selectedTab === 2 && (
                                <div className="flex flex-col md:flex-row w-full xl:pr-2 xl:pl-2 mt-4">
                                    <div className="float-left w-full flex-col">
                                        <ClientStatisticsPopUp
                                            statistics={clientstats}
                                        />
                                    </div>
                                </div>
                            )}
                            {selectedTab === 4 && (
                                <div className="flex flex-col md:flex-row w-full xl:pr-2 xl:pl-2 mt-4">
                                    <CustomerDetails customer={clientInfo} />
                                </div>
                            )}
                            {selectedTab === 5 && (
                                <div className="flex flex-col md:flex-row w-full xl:pr-2 xl:pl-2 mt-4 justify-center items-center">
                                    <p className="mt-20">
                                        Bientôt disponible, nous vous
                                        informerons dès qu&apos;il sera en
                                        ligne.
                                    </p>
                                </div>
                            )}
                        </Box>
                    </Box>

                    {/* Popup overlay */}
                    {showStatsPopUp && (
                        <ClientStatisticsPopUp
                            statistics={clientstats}
                            togglePopup={setShowStatsPopUp}
                        />
                    )}
                    {showCadencierPopUp && (
                        <CadencierPopUp
                            setShowPopUp={setShowCadencierPopUp}
                            clientOrders={clientorders}
                        />
                    )}
                    {showFacturePopUp && (
                        <FacturePopUp
                            setShowPopUp={setShowFacturePopUp}
                            receipts={clientreceipts}
                        />
                    )}

                    {message && (
                        <div
                            className={`fixed bottom-10 left-1/2 transform -translate-x-1/2 p-4 rounded-md text-white ${
                                messageType === "success"
                                    ? "bg-green-500"
                                    : "bg-red-500"
                            }`}
                            style={{ zIndex: 999 }}
                        >
                            {message}
                        </div>
                    )}

                    {!reportToggle && !reminderToggle && (
                        <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 md:left-auto md:right-5 md:transform-none flex-col items-center pb-2 md:pb-0 block md:hidden">
                            <div className="flex flex-row w-full sm:mt-5 sm:mb-5 md:mb-2 justify-end gap-x-4">
                                <button
                                    onClick={() => handleStartVisit()}
                                    className="group relative bg-gold w-14 h-14 rounded-full text-lg text-black font-semibold flex items-center justify-center drop-shadow-lg"
                                >
                                    <FiEdit className="text-2xl" />
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-black text-white text-xs rounded-md px-2 py-1">
                                        Compte rendu
                                    </span>
                                </button>

                                <button
                                    onClick={() => handleSetReminder()}
                                    className="group relative bg-black w-14 h-14 rounded-full text-lg text-gold font-semibold flex items-center justify-center drop-shadow-lg"
                                >
                                    <FiBell className="text-2xl" />
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-black text-white text-xs rounded-md px-2 py-1">
                                        Rappel
                                    </span>
                                </button>

                                <button
                                    onClick={openModal}
                                    className="group relative bg-black w-14 h-14 rounded-full text-lg text-gold font-semibold flex items-center justify-center drop-shadow-lg"
                                >
                                    <FiCalendar className="text-2xl" />
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-black text-white text-xs rounded-md px-2 py-1">
                                        RDV{" "}
                                    </span>
                                </button>

                                <button
                                    onClick={() => {
                                        if (shopURL?.shop_url) {
                                            window.open(
                                                shopURL.shop_url,
                                                "_blank"
                                            );
                                        } else {
                                            console.error(
                                                "shopURL.shop_url is missing or undefined!"
                                            );
                                        }
                                    }}
                                    className="group relative bg-black w-14 h-14 rounded-full text-lg text-gold font-semibold flex items-center justify-center drop-shadow-lg"
                                >
                                    <FiShoppingBag className="text-2xl" />
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-black text-white text-xs rounded-md px-2 py-1">
                                        Portail
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}
                    <div id="yourTargetDivId"></div>
                    {reportToggle && (
                        <section className="w-full bg-white flex flex-col items-center border-t border-gray-100  mt-5">
                            {reportlayout.length > 0 && (
                                <ReportForm
                                    reportlayout={reportlayout}
                                    setToggle={handleSaveVisit}
                                    clientId={clientId}
                                    meetingId={meetingId}
                                    userId={user.user_id}
                                    userName={user.first_name}
                                    meeting_type_id={meeting_type_id}
                                    setErrorMessage={setMessage} // Success message in French
                                    setErrorMessageType={setMessageType}
                                />
                            )}
                        </section>
                    )}
                    {reminderToggle && (
                        <section className="w-full bg-white flex flex-col items-center border-t border-gray-100  mt-5">
                            <ReminderModal
                                setReminderToggle={setReminderToggle}
                                userId={user.user_id}
                                clientId={clientId}
                                locale={locale}
                            />
                        </section>
                    )}
                    {openmodal && (
                        <section className="w-full bg-white flex flex-col items-center border-t border-gray-100  mt-5">
                            <MeetingModal
                                closeModal={closeModal}
                                locale={locale}
                                user_id={user.user_id}
                                initialEventData={newEventData}
                                detailsTrue={false}
                                user={user}
                                setSuccessMessage={setSuccessMessage}
                                setErrorMessage={setErrorMessage}
                                attendees={usersData}
                            />
                        </section>
                    )}
                    {successMessage && (
                        <div className="fixed bottom-0 left-0 right-0 bg-green-500 text-white text-center py-2 z-50 mx-auto w-1/5 rounded-t-lg">
                            {successMessage}
                        </div>
                    )}
                    {errorMessage && (
                        <div className="fixed bottom-0 left-0 right-0 bg-rose-500 text-white text-center py-2 z-50 mx-auto w-1/5 rounded-t-lg">
                            {errorMessage}
                        </div>
                    )}
                </IntlProvider>
            </div>
        </section>
    );
};

export default ClientPage;
